/**
 * ConnectionStatus — Top-right badge showing live network state.
 *
 * Renders a small pill with a coloured dot:
 *   - green dot + "<n>ms"          when ping < 80ms
 *   - yellow dot + "<n>ms"         when 80–200ms
 *   - red dot + "<n>ms" or "...""  when >200ms or unknown
 *   - red dot + "RECONNECTING"     while connection state is reconnecting
 *   - red dot + "OFFLINE"          when state is closed
 *
 * The component is fully passive — it only re-renders when callers invoke
 * `setPing()` / `setConnectionState()`. ClientNetManager subscribes its
 * PingMonitor and connection-state observable to drive this.
 */

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';
export type ConnectionTone = 'green' | 'yellow' | 'red';

export const PING_GREEN_MAX_MS = 80;
export const PING_YELLOW_MAX_MS = 200;

/**
 * Pure state-to-tone mapping. Extracted so unit tests can verify the rules
 * (the threshold ladder + reconnect override) without spinning up a DOM.
 */
export function computeConnectionTone(
  state: ConnectionState,
  pingMs: number,
  hasPing: boolean,
): ConnectionTone {
  if (state !== 'open') return 'red';
  if (!hasPing) return 'yellow';
  if (pingMs < PING_GREEN_MAX_MS) return 'green';
  if (pingMs <= PING_YELLOW_MAX_MS) return 'yellow';
  return 'red';
}

/** Pure label resolver matching {@link ConnectionStatus} renderer. */
export function computeConnectionLabel(
  state: ConnectionState,
  pingMs: number,
  hasPing: boolean,
): string {
  if (state === 'reconnecting') return 'RECONNECTING';
  if (state === 'closed') return 'OFFLINE';
  if (state === 'connecting') return 'CONNECTING';
  if (!hasPing) return '...';
  return `${pingMs}ms`;
}

export class ConnectionStatus {
  private container: HTMLElement;
  private dot: HTMLElement;
  private label: HTMLElement;
  private state: ConnectionState = 'connecting';
  private pingMs = 0;
  private hasPing = false;

  constructor(parentElement: HTMLElement) {
    this.container = document.createElement('div');
    this.container.className = 'connection-status-badge';

    this.dot = document.createElement('span');
    this.dot.className = 'connection-status-dot';
    this.container.appendChild(this.dot);

    this.label = document.createElement('span');
    this.label.className = 'connection-status-label';
    this.container.appendChild(this.label);

    parentElement.appendChild(this.container);
    this.render();
  }

  /** Update measured round-trip latency in milliseconds. */
  setPing(ms: number): void {
    this.pingMs = Math.max(0, Math.round(ms));
    this.hasPing = true;
    this.render();
  }

  setConnectionState(state: ConnectionState): void {
    this.state = state;
    // Stale ping must be cleared on every non-open transition — a
    // open → closed → connecting → open cycle would otherwise briefly
    // surface the pre-disconnect RTT sample during the connecting phase.
    if (state !== 'open') {
      this.hasPing = false;
    }
    this.render();
  }

  private render(): void {
    const tone = computeConnectionTone(this.state, this.pingMs, this.hasPing);
    this.container.dataset.tone = tone;
    this.label.textContent = computeConnectionLabel(this.state, this.pingMs, this.hasPing);
  }

  /** For tests + diagnostics. */
  getState(): { tone: ConnectionTone; label: string } {
    return {
      tone: computeConnectionTone(this.state, this.pingMs, this.hasPing),
      label: computeConnectionLabel(this.state, this.pingMs, this.hasPing),
    };
  }

  dispose(): void {
    this.container.remove();
  }
}
